/**
 * المسار مخطّط لا سلسلة: كل خطوة تعرف متطلباتها (`depends_on`) بمفاتيح خطوات
 * سابقة. الخطوات التي تشترك في المتطلبات نفسها تعمل متوازية، فتُجمَّع في
 * «مرحلة» واحدة عند العرض والتحرير.
 */

let uidSeq = 0;
export const newUid = () => `n${++uidSeq}`;

/** رقم مرحلة كل خطوة: أطول مسار يفصلها عن بداية المسار */
export const stageOf = (steps) => {
  const stage = {};
  (steps || []).forEach((s) => {
    const deps = s.depends_on || [];
    const levels = deps.map((d) => (stage[d] ?? 0));
    stage[s.key] = levels.length ? Math.max(...levels) + 1 : 0;
  });
  return stage;
};

/** الخطوات مجمّعةً في مراحل — كل مرحلة مصفوفة خطوات متوازية */
export const toStages = (steps) => {
  const stage = stageOf(steps);
  const stages = [];
  (steps || []).forEach((s) => {
    const i = stage[s.key] ?? 0;
    if (!stages[i]) stages[i] = [];
    stages[i].push(s);
  });
  return stages.filter((st) => st && st.length);
};

/** خطوات الخادم ← حالة المحرّر: `deps = null` يعني «كل خطوات المرحلة السابقة» */
export const toEditorStages = (steps) => {
  const stages = toStages(steps);
  return stages.map((stage, si) => {
    const prev = (stages[si - 1] || []).map((s) => s.key).sort();
    return stage.map((s) => {
      const deps = [...(s.depends_on || [])].sort();
      const inherits = deps.length === prev.length && deps.every((d, i) => d === prev[i]);
      return {
        uid: s.key,
        name: s.name || '',
        type: s.type || 'task',
        default_assignee: s.default_assignee || '',
        approver: s.approver || '',
        pay_item_id: s.pay_item_id || '',
        requires_audio: !!s.requires_audio,
        join: s.join === 'any' ? 'any' : 'all',
        deps: inherits ? null : [...(s.depends_on || [])],
      };
    });
  });
};

/** إسقاط المتطلبات المخصّصة التي لم تبقَ في مرحلة سابقة (بعد حذف أو نقل خطوة) */
export const sanitizeStages = (stages) =>
  stages
    .filter((stage) => stage.length)
    .map((stage, si, all) => {
      const earlier = new Set(all.slice(0, si).flat().map((s) => s.uid));
      return stage.map((s) => {
        if (!s.deps) return s;
        const deps = s.deps.filter((uid) => earlier.has(uid));
        return { ...s, deps: deps.length ? deps : null };
      });
    });

/** متطلبات الخطوة فعلياً: المخصّصة إن وُجدت، وإلّا كل خطوات المرحلة السابقة */
export const effectiveDeps = (stages, si, step) =>
  step.deps || (stages[si - 1] || []).map((s) => s.uid);

/** حالة المحرّر ← خطوات الخادم: مفاتيح بالموضع ومتطلبات صريحة لكل خطوة */
export const fromStages = (stages) => {
  const keyOf = {};
  let index = 0;
  stages.forEach((stage) => stage.forEach((s) => { keyOf[s.uid] = `s${index++}`; }));

  const steps = [];
  stages.forEach((stage, si) => {
    stage.forEach((s) => {
      const deps = effectiveDeps(stages, si, s).map((uid) => keyOf[uid]).filter(Boolean);
      const step = {
        key: keyOf[s.uid],
        name: (s.name || '').trim(),
        type: s.type || 'task',
        depends_on: deps,
        join: deps.length > 1 && s.join === 'any' ? 'any' : 'all',
      };
      if (s.type === 'task' && s.default_assignee) step.default_assignee = s.default_assignee;
      if (s.type === 'task' && s.pay_item_id) step.pay_item_id = Number(s.pay_item_id);
      if (s.type === 'task' && s.requires_audio) step.requires_audio = true;
      if (s.type === 'approval' && s.approver) step.approver = s.approver;
      steps.push(step);
    });
  });
  return steps;
};
